import type {
  PromotionFilters,
  PromotionLifecycleStatus,
  PromotionResponse,
  PromotionStateFilter,
  PromotionValidationDetail,
} from '@/services/promotionsApi'

const PROMOTION_STATE_TITLES: Record<PromotionLifecycleStatus, string> = {
  inactive: 'Inactiva',
  current: 'Vigente',
  upcoming: 'Próxima',
  expired: 'Expirada',
}

const PROMOTION_STATE_TONES: Record<PromotionLifecycleStatus, 'slate' | 'emerald' | 'amber' | 'rose'> = {
  inactive: 'slate',
  current: 'emerald',
  upcoming: 'amber',
  expired: 'rose',
}

const PROMOTION_FIELD_LABELS: Record<string, string> = {
  name: 'nombre',
  description: 'descripción',
  discountPct: 'descuento porcentual',
  discountAmt: 'descuento fijo',
  productId: 'producto',
  validFrom: 'vigencia inicial',
  validUntil: 'vigencia final',
  active: 'estado',
}

export function countActivePromotionFilters(filters: PromotionFilters): number {
  let count = 0

  if (filters.state && filters.state !== 'all') count += 1
  if (filters.search?.trim()) count += 1
  if (filters.productId?.trim()) count += 1

  return count
}

export function getPromotionLifecycleBadge(status: PromotionLifecycleStatus) {
  return {
    label: PROMOTION_STATE_TITLES[status] ?? 'Inactiva',
    tone: PROMOTION_STATE_TONES[status] ?? 'slate',
  }
}

export function getPromotionDiscountLabel(promotion: Pick<PromotionResponse, 'discountPct' | 'discountAmt'>) {
  if (promotion.discountPct !== null && promotion.discountPct !== undefined) {
    return `${promotion.discountPct}%`
  }

  if (promotion.discountAmt !== null && promotion.discountAmt !== undefined) {
    return `Monto fijo ${promotion.discountAmt}`
  }

  return 'Sin descuento'
}

export function getPromotionTargetLabel(productName: string | null | undefined) {
  return productName?.trim() ? productName : 'Promoción general'
}

export function getPromotionLifecycleDescription(status: PromotionLifecycleStatus) {
  if (status === 'current') return 'Activa y dentro de vigencia'
  if (status === 'upcoming') return 'Activa pero aún no inicia'
  if (status === 'expired') return 'La fecha de vigencia ya terminó'
  return 'Desactivada por el equipo'
}

export function formatPromotionDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Sin fecha'
  }

  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function getPromotionEmptyState(
  total: number,
  hasActiveFilters: boolean,
  context?: { productId?: string | null; productName?: string | null }
) {
  if (total > 0) {
    return null
  }

  if (context?.productId?.trim()) {
    const targetName = context.productName?.trim() ? ` para "${context.productName.trim()}"` : ' para este producto'
    return {
      title: `Sin promociones${targetName}`,
      description: 'Crea una promoción exclusiva para este producto o limpia el filtro para ver las demás.',
      primaryAction: 'Crear promoción para este producto',
      secondaryAction: 'Limpiar filtros',
    }
  }

  if (hasActiveFilters) {
    return {
      title: 'No hay coincidencias',
      description: 'Prueba con menos filtros o limpia la búsqueda para ver más promociones.',
      primaryAction: 'Limpiar filtros',
      secondaryAction: 'Limpiar filtros',
    }
  }

  return {
    title: 'Aún no hay promociones',
    description: 'Crea una promoción por producto o para toda la tienda y publícala desde aquí.',
    primaryAction: 'Nueva promoción',
    secondaryAction: 'Ir a catálogo',
  }
}

export function humanizePromotionValidationField(field: string) {
  const normalized = field.replace(/\.\d+\./g, '.').replace(/\.\d+$/g, '')
  return PROMOTION_FIELD_LABELS[normalized] ?? PROMOTION_FIELD_LABELS[field] ?? field
}

export function buildPromotionValidationMessage(details: PromotionValidationDetail[] | undefined | null) {
  if (!details || details.length === 0) {
    return 'No se pudo guardar la promoción'
  }

  const labels = Array.from(
    new Set(
      details
        .map((detail) => humanizePromotionValidationField(detail.field))
        .filter((label) => Boolean(label?.trim()))
    )
  )

  if (labels.length === 0) {
    return 'No se pudo guardar la promoción'
  }

  return `Falta completar ${labels.length === 1 ? 'el campo' : 'los campos'}: ${labels.join(', ')}`
}

export function getPromotionStateOptions(): Array<{ value: PromotionStateFilter; label: string }> {
  return [
    { value: 'all', label: 'Todas' },
    { value: 'current', label: 'Vigentes' },
    { value: 'upcoming', label: 'Próximas' },
    { value: 'expired', label: 'Expiradas' },
    { value: 'active', label: 'Activas' },
    { value: 'inactive', label: 'Inactivas' },
  ]
}
